import { useTranslations } from "@fenetech/translations";
import ESignatureAPI from "components/Setup/ESignature/ESignatureAPI";
import useApplicationInfo from "helpers/context/Application/useApplicationInfo";
import useWait from "helpers/context/Page/useWait";
import { DocuSignEnvelopeStatusEnum, ESignatureRedirectSourceEnum, RoleEnum, ThemeColorEnum } from "helpers/enums";
import { GetParamCaseInsensitive, isNullOrWhiteSpace } from "helpers/objects";
import { IESignatureDocumentStatus } from "models/IESignature";
import React, { useCallback, useEffect, useMemo } from 'react';
import { MaintenanceData } from "./useMaintenanceData";
import { useSearchParams } from "react-router-dom";
import { useEffectOnLoad } from "helpers/hooks/useEffectOnLoad";
import { AlertColor } from "@mui/material";
import useMessageBox from "helpers/context/Page/useMessageBox";
import useUserInfo from "helpers/context/User/useUserInfo";

interface IProps {
    eSignatureStatuses: IESignatureDocumentStatus[],
    setESignatureStatuses: React.Dispatch<React.SetStateAction<IESignatureDocumentStatus[]>>,
    pagedData: MaintenanceData | undefined,
    setAlert: React.Dispatch<React.SetStateAction<[text: string, alertType: AlertColor] | null>>
}

const useESignatureMaintenanceHelper = ({ eSignatureStatuses, setESignatureStatuses, pagedData, setAlert }: IProps) => {

    const tm = useTranslations();
    const appInfo = useApplicationInfo();
    const wait = useWait();
    const [query] = useSearchParams();
    const messageBox = useMessageBox();
    const user = useUserInfo();

    const eSignMessage: string | null = useMemo(() => {
        return GetParamCaseInsensitive(query, "eSignMessage") ?? null;
    }, [query]);

    useEffectOnLoad(() => {
        //Check if we have a message when loading in. If so, we just came from the 3rd party login
        if (!isNullOrWhiteSpace(eSignMessage)) {
            setAlert([eSignMessage!, ThemeColorEnum.Error]);
        }
    });

    const eSignatureEnabled: boolean = useMemo(() => {
        return appInfo.parameters.eSignatureEnabled && user.HasRole(RoleEnum.Dealer);
    }, [appInfo, user]);

    const callbackURI = useMemo(() => {
        return document.getElementsByTagName("base")[0].href + "setup/esign/callback";
    }, []);

    const getESignatureStatuses = useCallback(async () => {
        if (pagedData) {
            try {
                wait.Show(true);
                const anyData = pagedData.data as any[];
                const oKeys: number[] = anyData.map(q => q["oKey"]);
                const result = await ESignatureAPI.GetMaintenanceStatusList(oKeys);
                setESignatureStatuses(result);
            } finally {
                wait.Show(false);
            }
        }
    }, [pagedData, setESignatureStatuses, wait]);

    useEffect(() => {
        if (eSignatureEnabled) {
            getESignatureStatuses();
        }
    }, [eSignatureEnabled, getESignatureStatuses]);

    const launchESignatureConsentLogin = useCallback(async (oKey: number) => {
        const authResponse = await ESignatureAPI.GetAuthURIFromOKey(oKey, callbackURI, ESignatureRedirectSourceEnum.Maintenance);
        if (!isNullOrWhiteSpace(authResponse.message)) {
            setAlert([tm.Get(authResponse.message), ThemeColorEnum.Error]);
        } else {
            window.location.assign(authResponse.payload);
        }
    }, [callbackURI, tm, setAlert]);

    const getESignatureConsentLoginConfirmationPrompt = useCallback((oKey: number) => {
        const anyData = pagedData?.data as any[];
        const mfgCustomerName = anyData.find(p => p.oKey === oKey)?.mfgSiteName ?? "";

        return {
            message: `${tm.Get("E-Signature login required for Mfg Customer:")} ${mfgCustomerName}`,
            title: tm.Get("E-Signature"),
            visible: true,
            okCancelPrompt: true,
            callback: ((dialogResult?: boolean) => { if (dialogResult) launchESignatureConsentLogin(oKey)})
        }
    }, [launchESignatureConsentLogin, tm, pagedData]);

    const onRefreshESignatureStatus = useCallback(async (oKey: number, signatureStatus: DocuSignEnvelopeStatusEnum, documentReferenceID: string) => {
        try {
            wait.Show(true);
            const eSignatureDocumentStatus: IESignatureDocumentStatus = {
                status: signatureStatus,
                oKey: oKey,
                documentReferenceID: documentReferenceID
            }
            const refreshResult = await ESignatureAPI.RefreshDocumentStatus(eSignatureDocumentStatus);
            if (refreshResult.loginRequested) {
                await launchESignatureConsentLogin(oKey);
            } else if (!isNullOrWhiteSpace(refreshResult.message)) {
                setAlert([tm.Get(refreshResult.message), ThemeColorEnum.Error]);
            } else {
                // Need a new array reference so react picks up the change
                const currentStatuses: IESignatureDocumentStatus[] = [...eSignatureStatuses];
                const index = currentStatuses.findIndex(s => s.documentReferenceID === refreshResult.payload.documentReferenceID);
                if (index !== -1) {
                    currentStatuses[index].status = refreshResult.payload.status;
                }
                setESignatureStatuses(currentStatuses);
            }
        }
        finally {
            wait.Show(false);
        }
    }, [launchESignatureConsentLogin, eSignatureStatuses, setAlert, tm, wait, setESignatureStatuses]);

    const onRefreshAllESignatureStatuses = useCallback(async () => {
        if (eSignatureStatuses && eSignatureStatuses.length > 0) {
            wait.Show(true);
            try {
                const newStatusesResult = await ESignatureAPI.RefreshDocumentStatuses(eSignatureStatuses);
                if (newStatusesResult.loginRequested) {
                    //If we got login requested during bulk operation, the message has the Okey that needs a new access token
                    const oKey: number = Number.parseInt(newStatusesResult.message);
                    const confirmationPrompt = getESignatureConsentLoginConfirmationPrompt(oKey);
                    messageBox.Show(confirmationPrompt);
                } else if (!isNullOrWhiteSpace(newStatusesResult.message)) {
                    setAlert([tm.Get(newStatusesResult.message), ThemeColorEnum.Error]);
                } else {
                    await getESignatureStatuses();
                    setAlert([tm.Get("E-Signature documents up-to-date"), ThemeColorEnum.Success]);
                }
            }
            finally {
                wait.Show(false);
            }
        }
    }, [eSignatureStatuses, getESignatureConsentLoginConfirmationPrompt, tm, wait, getESignatureStatuses, setAlert, messageBox]);

    const onRefreshESignatureStatusesForUpload = useCallback(async (oKeys: number[]) => {
        let loginRequired: boolean = false;
        if (eSignatureStatuses && eSignatureStatuses.length > 0) {
            wait.Show(true);
            try {
                const statusesToRefresh = eSignatureStatuses.filter(es => oKeys.includes(es.oKey));
                const newStatusesResult = await ESignatureAPI.RefreshDocumentStatuses(statusesToRefresh);
                if (newStatusesResult.loginRequested) {
                    //If we got login requested during bulk operation, the message has the Okey that needs a new access token
                    const oKey: number = Number.parseInt(newStatusesResult.message);
                    const confirmationPrompt = getESignatureConsentLoginConfirmationPrompt(oKey);
                    messageBox.Show(confirmationPrompt);
                    loginRequired = true;
                } else if (!isNullOrWhiteSpace(newStatusesResult.message)) {
                    setAlert([tm.Get(newStatusesResult.message), ThemeColorEnum.Error]);
                }
            }
            finally {
                wait.Show(false);
            }
        }
        return loginRequired;
    }, [eSignatureStatuses, getESignatureConsentLoginConfirmationPrompt, tm, wait, setAlert, messageBox]);

    return {
        eSignatureEnabled,
        onRefreshESignatureStatus,
        onRefreshAllESignatureStatuses,
        onRefreshESignatureStatusesForUpload
    }
}

export default useESignatureMaintenanceHelper;